import axios, {AxiosError, AxiosResponse} from "axios";
import Accordion from "../models/Accordion";
import Image from "../models/Image";
import {User, UserCredentials} from "../models/User";
import {ContactFormEntry} from "../models/ContactFormEntry";
import {CustomEvents, eventEmitter} from "../events/events";
import {CreditText} from "../models/CreditText";
import {Setting} from "../models/Setting";

const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay)
    })
}

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
const responseBody = (response: AxiosResponse) => {
    try {
        if(!response) return;
        switch (response.status) {
            case 200:
                if(response.config.method?.toLowerCase() !== 'get') eventEmitter.emit(CustomEvents.GLOBALMESSAGE, 'success', 'Successfully completed')
                return response.data;
            case 201:
                if(response.config.method?.toLowerCase() !== 'get') eventEmitter.emit(CustomEvents.GLOBALMESSAGE, 'success', 'Successfully completed')
                return response.data;
            default:
                eventEmitter.emit(CustomEvents.GLOBALMESSAGE, 'error', response.data)
                console.error(response.data);
        }
    } catch (e) {
        console.error(e)
        eventEmitter.emit(CustomEvents.GLOBALMESSAGE, 'error', e);
    }
};

axios.interceptors.request.use(config => {
    const token = sessionStorage.getItem('piginiChToken');
    if(token && config.headers) config.headers.Authorization = `Bearer ${token}`;
    return config;
})

axios.interceptors.response.use(async response => {
    if(process.env.NODE_ENV === 'development') await sleep(2000);
    return response;
}, (error: AxiosError) => {
    eventEmitter.emit(CustomEvents.GLOBALMESSAGE, 'error', error.message)
    console.error(error.message);
})

const requests = {
    get: (url: string) => axios.get(url).then(responseBody),
    post: (url: string, body: {}) => axios.post(url, body).then(responseBody),
    put: (url: string, body: {}) => axios.put(url, body).then(responseBody),
    del: (url: string) => axios.delete(url).then(responseBody)
}

const Accordions = {
    findAll: (): Promise<Accordion[]> => requests.get('Accordions'),
    create: (): Promise<Accordion> => requests.post('Accordions', {}),
    update: (accordion: Accordion): Promise<Accordion> => requests.put(`Accordions`, accordion),
    del: (id: number): Promise<number> => requests.del(`Accordions/${id}`),
    setAsThumbnail: (accordionId: number, imageId: number): Promise<Accordion> => requests.put(`Accordions/${accordionId}/thumbnail/${imageId}`, {})
}

const Images = {
    findOfAccordion: (accordionId: number): Promise<Image[]> => requests.get(`Images/Accordions/${accordionId}`),
    create: (image: Image): Promise<Image> => requests.post(`Images`, image),
    del: (id: number): Promise<number> => requests.del(`Images/${id}`)
}

const Account = {
    login: (credentials: UserCredentials): Promise<User> => requests.post('/account/login', credentials),
}

const ContactForm = {
    post: (contactFormEntry: ContactFormEntry): Promise<ContactFormEntry> => requests.post('ContactForm', contactFormEntry),
}

const CreditTexts = {
    findAll: (): Promise<CreditText[]> => requests.get('CreditTexts'),
    create: (language: 'ge' | 'fr' | 'ru' | 'en'): Promise<CreditText> => requests.post('CreditTexts', { language: language }),
    update: (creditText: CreditText): Promise<CreditText[]> => requests.put('CreditTexts', creditText),
    del: (id: number): Promise<CreditText[]> => requests.del(`CreditTexts/${id}`),
}

const Settings = {
    findAll: (): Promise<Setting[]> => requests.get('Settings'),
    update: (entity: Setting): Promise<Setting> => {
        return requests.put('Settings', entity);
    },
}

const agent = {
    Accordions: Accordions,
    Images: Images,
    Account: Account,
    ContactForm: ContactForm,
    CreditTexts: CreditTexts,
    Settings: Settings
}

export default agent;